/**
 * Pure helpers extracted from <SetupWizard> so we can exercise step
 * validation, step-id ordering, and stepDataFor() under vitest
 * without a React renderer (the workspace ships without
 * @testing-library/react — see -memories-auth-gate.test.tsx).
 */

import { getApiErrorCode, type ApiClientError } from '../../lib/api-error';

export type StepId =
  | 'welcome'
  | 'sysadmin'
  | 'tenant'
  | 'profile'
  | 'model'
  | 'users'
  | 'features'
  | 'data_source'
  | 'smoke_test'
  | 'complete';

export const STEP_ORDER: readonly StepId[] = [
  'welcome',
  'sysadmin',
  'tenant',
  'profile',
  'model',
  'users',
  'features',
  'data_source',
  'smoke_test',
  'complete',
] as const;

export interface SetupValidationForm {
  acceptedTerms: boolean;
  acceptedPrivacy: boolean;
  sysadminEmail: string;
  sysadminPassword: string;
  sysadminPasswordConfirm: string;
  tenantName: string;
  tenantSlug: string;
  tenantOrgCode: string;
  deploymentProfile: 'cpu_only' | 'standard_gpu' | 'high_perf' | 'edge';
  primaryModel: string;
  allowWebSearch: boolean;
  smokeOk: boolean;
}

/**
 * Determine whether the wizard's Next button is enabled for a given
 * step. Mirrors the inline `canAdvance()` switch in <SetupWizard>.
 *
 * Pure: no React deps, no DOM access. Test surface for validation.
 */
export function canAdvanceStep(
  stepId: StepId,
  form: SetupValidationForm,
  ctx: { gpuAvailable: boolean },
): boolean {
  switch (stepId) {
    case 'welcome':
      return form.acceptedTerms && form.acceptedPrivacy;
    case 'sysadmin':
      return (
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.sysadminEmail) &&
        form.sysadminPassword.length >= 12 &&
        form.sysadminPassword === form.sysadminPasswordConfirm
      );
    case 'tenant':
      return (
        form.tenantName.trim().length >= 2 &&
        /^[a-z][a-z0-9-]{2,49}$/.test(form.tenantSlug) &&
        /^[A-Z][A-Z0-9-]{2,29}$/.test(form.tenantOrgCode)
      );
    case 'profile':
      return ['cpu_only', 'standard_gpu', 'high_perf', 'edge'].includes(form.deploymentProfile);
    case 'model':
      return form.primaryModel.trim().length > 0 || !ctx.gpuAvailable;
    case 'users':
      return true;
    case 'features':
      // Both settings are plain booleans with real defaults, so there is
      // nothing to refuse — the step is always advanceable. (It used to
      // require `featureAskArgus`, a toggle the founder abolished.)
      return true;
    case 'data_source':
      return true;
    case 'smoke_test':
      return form.smokeOk;
    case 'complete':
      return true;
    default:
      return false;
  }
}

/** Compute the next step id after the given one (clamped at `complete`). */
export function nextStepId(current: StepId): StepId {
  const idx = STEP_ORDER.indexOf(current);
  if (idx < 0 || idx >= STEP_ORDER.length - 1) return 'complete';
  return STEP_ORDER[idx + 1];
}

/** Auto-derive a slug from a tenant display name (max 50 chars). */
export function slugifyTenantName(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .slice(0, 50);
}

/** Auto-derive an org code from a tenant display name (max 30 chars). */
export function orgCodeFromName(name: string): string {
  return name
    .toUpperCase()
    .replace(/\s+/g, '-')
    .replace(/[^A-Z0-9-]/g, '')
    .slice(0, 30);
}

/** Score a candidate password (5-point scale). */
export function passwordStrengthScore(pw: string): number {
  let score = 0;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score;
}

/**
 * True when GET /setup/state refused because this box is already set up:
 * 410 SETUP_ALREADY_COMPLETE (the completion flag is set) or 409
 * SYSADMIN_ALREADY_EXISTS (an administrator exists even if the flag lags) —
 * the two refusals POST /setup/step gives (decision 20). The wizard then
 * leaves instead of rendering step 1.
 *
 * Reads the error through the shared helpers in lib/api-error.ts (the one
 * place the apiClient error shape is described), never a private cast. The
 * code alone is enough, so a proxy that rewrites the status still matches;
 * a bare 410 with no body matches too.
 */
export function isSetupClosedError(err: unknown): boolean {
  const code = getApiErrorCode(err);
  if (code === 'SETUP_ALREADY_COMPLETE' || code === 'SYSADMIN_ALREADY_EXISTS') return true;
  return (err as ApiClientError | undefined)?.status === 410;
}
